import React from "react";
import { Box, Grid, Typography, Divider } from "@mui/material";

const ObjectFieldTemplate = (props) => {
  const { title, description, properties, uiSchema } = props;

  // Section titles that should be styled as headers
  const sectionTitles = {
    "Agent Data Intake": true,
  };

  // Group properties by section for better organization
  const sections = [];
  let currentSection = { title: null, fields: [] };

  properties.forEach((element, index) => {
    const fieldName = element.content.key;

    // Define section breaks - matching original form structure
    const sectionBreaks = {
      agentType: null, // First field, no header
      existingAgentId: "Agent Information",
      optimedAgentId: "Agent Information",
      carrierSold: "Group Information",
      carrierOfProduct: "Group Information",
      updateAgentLicense: "Update Documents",
      isLicensedAgent: null, // No header before this
      fillerName: "Your Information",
      agentFirstName: "Licensed Agent Legal Name",
      agentNPN: "Licensed Agent Information",
      agentEmail: "Licensed Agent Contact Information",
      agentSex: "Licensed Agent Demographics",
      agentHomeAddress: "Licensed Agent Home Address",
      isAgencyOwner: "Agency Ownership",
      ownerFirstName: "Agency Owner Information",
      agencyName: "Agency Information",
      agencyAddress: "Agency Address",
      operatingAs: "Business Structure",
      backgroundQ1: "Background Questions",
      uploadAgentLicense: "Required Documents - Agent License",
      uploadAgencyLicense: "Required Documents - Agency License",
      uploadEO: "Required Documents - E&O Insurance",
      uploadW9: "Required Documents - Tax Forms",
      uploadVoidCheck: "Required Documents - Banking",
      uploadNationwideContract: "Required Documents - Carrier Appointments",
    };

    if (sectionBreaks[fieldName]) {
      if (currentSection.fields.length > 0) {
        sections.push(currentSection);
      }
      currentSection = { title: sectionBreaks[fieldName], fields: [element] };
    } else {
      currentSection.fields.push(element);
    }

    // Add the last section
    if (index === properties.length - 1 && currentSection.fields.length > 0) {
      sections.push(currentSection);
    }
  });

  return (
    <Box>
      {title && !sectionTitles[title] && (
        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}

      {sections.map((section, sectionIndex) => {
        // Check if all fields in this section are hidden
        const allFieldsHidden = section.fields.every((field) => {
          const fieldName = field.content.key;
          return uiSchema[fieldName]?.["ui:widget"] === "hidden";
        });

        // Don't render the section at all if all fields are hidden
        if (allFieldsHidden) {
          return null;
        }

        return (
          <Box key={sectionIndex} sx={{ mb: 3 }}>
            {section.title && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 3,
                    mb: 2,
                    color: "primary.main",
                    fontWeight: 600,
                    fontSize: "1.1rem",
                  }}
                >
                  {section.title}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            <Grid container spacing={2}>
              {section.fields.map((element, index) => {
                const fieldName = element.content.key;

                // Contact fields should be in a 3-column grid
                const contactFields = ["contactEmail", "contactPhone", "contactAgency"];

                // Group Information fields should be in a 3-column grid
                const groupInfoFields = ["carrierOfProduct", "groupName", "stateGroupSoldIn"];

                // Agency tax/license fields should be in a 3-column grid
                const agencyTaxFields = ["agencyNPN", "hasDBA", "taxId"];

                // Agency address city/state/zip fields should be in a 3-column grid
                const agencyAddressFields = ["agencyCity", "agencyState", "agencyZip"];

                // Check if this is one of the contact fields
                const isContactField = contactFields.includes(fieldName);

                // Check if this is one of the group info fields
                const isGroupInfoField = groupInfoFields.includes(fieldName);

                // Check if this is one of the agency tax fields
                const isAgencyTaxField = agencyTaxFields.includes(fieldName);

                // Check if this is one of the agency address fields
                const isAgencyAddressField = agencyAddressFields.includes(fieldName);

                // Check if the previous and next fields are also contact fields for grouping
                const prevField = section.fields[index - 1]?.content.key;
                const nextField = section.fields[index + 1]?.content.key;
                const isFirstContactField = isContactField && !contactFields.includes(prevField);
                const isLastContactField = isContactField && !contactFields.includes(nextField);

                // Check if this is the first group info field
                const isFirstGroupInfoField = isGroupInfoField && !groupInfoFields.includes(prevField);

                // Check if this is the first agency tax field
                const isFirstAgencyTaxField = isAgencyTaxField && !agencyTaxFields.includes(prevField);

                // Check if this is the first agency address field
                const isFirstAgencyAddressField = isAgencyAddressField && !agencyAddressFields.includes(prevField);

                // If this is the first contact field, start a new container
                if (isFirstContactField) {
                  const contactFieldElements = section.fields
                    .slice(index, index + 3)
                    .filter((el) => contactFields.includes(el.content.key));

                  return (
                    <Grid size={12} key={`contact-grid-${index}`}>
                      <Grid container spacing={2}>
                        {contactFieldElements.map((contactEl, contactIndex) => (
                          <Grid size={{ xs: 12, md: 4 }} key={`contact-${contactIndex}`}>
                            {contactEl.content}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  );
                }

                // If this is the first group info field, start a new container
                if (isFirstGroupInfoField) {
                  const groupInfoElements = section.fields
                    .slice(index, index + 3)
                    .filter((el) => groupInfoFields.includes(el.content.key));

                  return (
                    <Grid size={12} key={`group-info-grid-${index}`}>
                      <Grid container spacing={2}>
                        {groupInfoElements.map((groupEl, groupIndex) => (
                          <Grid size={{ xs: 12, md: 4 }} key={`group-${groupIndex}`}>
                            {groupEl.content}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  );
                }

                // If this is the first agency tax field, start a new container
                if (isFirstAgencyTaxField) {
                  const agencyTaxElements = section.fields
                    .slice(index, index + 3)
                    .filter((el) => agencyTaxFields.includes(el.content.key));

                  return (
                    <Grid size={12} key={`agency-tax-grid-${index}`}>
                      <Grid container spacing={2}>
                        {agencyTaxElements.map((taxEl, taxIndex) => (
                          <Grid size={{ xs: 12, md: 4 }} key={`tax-${taxIndex}`}>
                            {taxEl.content}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  );
                }

                // If this is the first agency address field, start a new container
                if (isFirstAgencyAddressField) {
                  const agencyAddressElements = section.fields
                    .slice(index, index + 3)
                    .filter((el) => agencyAddressFields.includes(el.content.key));

                  return (
                    <Grid size={12} key={`agency-address-grid-${index}`}>
                      <Grid container spacing={2}>
                        {agencyAddressElements.map((addrEl, addrIndex) => (
                          <Grid size={{ xs: 12, md: 4 }} key={`addr-${addrIndex}`}>
                            {addrEl.content}
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  );
                }

                // Skip rendering individual contact fields after the first one (they're rendered in the group)
                if (isContactField && !isFirstContactField) {
                  return null;
                }

                // Skip rendering individual group info fields after the first one (they're rendered in the group)
                if (isGroupInfoField && !isFirstGroupInfoField) {
                  return null;
                }

                // Skip rendering individual agency tax fields after the first one (they're rendered in the group)
                if (isAgencyTaxField && !isFirstAgencyTaxField) {
                  return null;
                }

                // Skip rendering individual agency address fields after the first one (they're rendered in the group)
                if (isAgencyAddressField && !isFirstAgencyAddressField) {
                  return null;
                }

                // Fields that should take full width
                const fullWidthFields = [
                  "agentType",
                  "updateType",
                  "carriers",
                  "bankruptcyExplanation",
                  "criminalExplanation",
                  "licenseExplanation",
                  "regulatoryExplanation",
                  "terminationExplanation",
                  "updateCommissionNotes",
                  "agencyWebsite",
                  "pdbConsent",
                  "achInstructions",
                  "optimedAgentId",
                  "groupNumber",
                  "isLicensedAgent",
                  "agencyName",
                  "dbaName",
                  "agencyAddress",
                  "agencyAddress2",
                  "operatingAs",
                  "backgroundQ1",
                  "backgroundQ2",
                  "backgroundQ3",
                  "backgroundQ4",
                  "backgroundQ5",
                  "backgroundQ6",
                  "backgroundQ7",
                ];

                // Fields that should take half width
                const halfWidthFields = [
                  "hasHomeAddress",
                  "isAgencyOwner",
                  "bankruptcyHistory",
                  "criminalHistory",
                  "licenseDiscipline",
                  "regulatoryAction",
                  "terminatedForCause",
                  "gender",
                  "commissionStructure",
                  "updateCommissionStructure",
                ];

                let gridSize = 6; // Default to half width (2 columns)

                if (fullWidthFields.includes(fieldName)) {
                  gridSize = 12; // Full width
                } else if (halfWidthFields.includes(fieldName)) {
                  gridSize = 6; // Half width
                } else if (fieldName?.startsWith("upload")) {
                  gridSize = 6; // Half width for uploads
                } else {
                  gridSize = 6; // Default half width for cleaner 2-column layout
                }

                return (
                  <Grid size={{ xs: 12, sm: gridSize }} key={index}>
                    {element.content}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

export default ObjectFieldTemplate;
